import { Order, WritingType } from "src/app/types/orders/order";
import { arrayOfObjectAreEqual } from "../objectHelper";
import { countWord } from "../stringHelper";
import getConstValues from "./getConstValues";
import { TDocMetrics } from "./handleData";
import { Content, ImageContent } from "src/app/types/content";
import { UserRole } from "src/app/transports/user";

type TValues = {
    metaTitle: string;
    title: string;
    metaDescription: string;
};

export const getTextValidation = (
    isForReview: boolean,
    values: TValues,
    docMetrics?: TDocMetrics,
    entity?: Order,
    checkMinimumWords = true
) => {
    const {
        NUMBER_OF_WORDS_PER_H2,
        NUMBER_OF_WORDS_PER_H3,
        MAX_NUMBER_OF_MINIMAL_H2,
        MAX_NUMBER_OF_MINIMAL_H3,
        NUMBER_OF_KEYWORD_PER_TEXT,
        MIN_NUMBER_OF_CHAR_IN_META_TITLE,
        MAX_NUMBER_OF_CHAR_IN_META_TITLE,
        MIN_NUMBER_OF_CHAR_IN_META_DESC,
        MAX_NUMBER_OF_CHAR_IN_META_DESC,
        MAX_LIST,
        NUMBER_OF_WORDS_BOLD,
        NUMBER_OF_WORDS_ITALIC,
    } = getConstValues();

    return {
        wordNumber:
            (docMetrics?.numberOfWords || 0) +
            countWord(values.metaTitle) +
            countWord(values.metaDescription) +
            countWord(values.title),
        get wordNumberCheckPass() {
            return (
                !checkMinimumWords ||
                (this.wordNumber >= (entity?.wordCount || 0) &&
                    this.wordNumber <= Math.floor((entity?.wordCount || 0) * 1.6))
            );
        },

        //Meta Title
        metaTitleCharNumber: values.metaTitle?.length,
        get metaTitleCheckPass() {
            return (
                this.metaTitleCharNumber >= MIN_NUMBER_OF_CHAR_IN_META_TITLE &&
                this.metaTitleCharNumber <= MAX_NUMBER_OF_CHAR_IN_META_TITLE
            );
        },

        //Meta Description
        metaDescriptionCharNumber: values.metaDescription?.length || 0,
        get metaDescriptionCheckPass() {
            return (
                this.metaDescriptionCharNumber >= MIN_NUMBER_OF_CHAR_IN_META_DESC &&
                this.metaDescriptionCharNumber <= MAX_NUMBER_OF_CHAR_IN_META_DESC
            );
        },

        //h2 Handlers
        h2Count: docMetrics?.numberOfH2 || 0,
        numberOfInvalidH2: docMetrics?.numberOfInvalidH2 || 0,
        existInvalidH2: docMetrics?.invalidH2,
        h2CountNeeded:
            Math.floor((entity?.wordCount || 0) / NUMBER_OF_WORDS_PER_H2) >=
            MAX_NUMBER_OF_MINIMAL_H2
                ? MAX_NUMBER_OF_MINIMAL_H2
                : Math.floor((entity?.wordCount || 0) / NUMBER_OF_WORDS_PER_H2),
        get h2CountCheckPass() {
            return this.h2Count >= this.h2CountNeeded;
        },

        //H3 Handlers
        h3Count: docMetrics?.numberOfH3 || 0,
        existInvalidH3: docMetrics?.invalidH3,
        numberOfInvalidH3: docMetrics?.numberOfInvalidH3 || 0,
        h3CountNeeded:
            (entity?.wordCount || 0) < NUMBER_OF_WORDS_PER_H3
                ? 0
                : Math.floor(1.5 * Math.floor((entity?.wordCount || 0) / NUMBER_OF_WORDS_PER_H2)) >=
                  MAX_NUMBER_OF_MINIMAL_H3
                ? MAX_NUMBER_OF_MINIMAL_H3
                : Math.floor(1.5 * Math.floor((entity?.wordCount || 0) / NUMBER_OF_WORDS_PER_H2)),
        get h3CountCheckPass() {
            return this.h3Count >= this.h3CountNeeded;
        },

        //Bold Handlers
        boldCount: docMetrics?.numberOfBold || 0,
        existInvalidBold: docMetrics?.invalidBold,
        numberOfInvalidBold: docMetrics?.numberOfInvalidBold || 0,
        boldCountNeeded: NUMBER_OF_WORDS_BOLD,
        get boldCountCheckPass() {
            return NUMBER_OF_WORDS_BOLD <= this.boldCount;
        },

        //italic Handlers
        italicCount: docMetrics?.numberOfItalic || 0,
        existInvalidItalic: docMetrics?.invalidItalic,
        numberOfInvalidItalic: docMetrics?.numberOfInvalidItalic || 0,
        italicCountNeeded: NUMBER_OF_WORDS_ITALIC,
        get italicCountCheckPass() {
            return NUMBER_OF_WORDS_ITALIC <= this.italicCount;
        },

        //list Handlers
        listCount:
            (docMetrics?.numberOfUnorderedList || 0) + (docMetrics?.numberOfOrderedList || 0),
        existInvalidList: docMetrics?.invalidUnorderedList || docMetrics?.invalidOrderedList,
        numberOfInvalidList:
            (docMetrics?.numberOfInvalidUnorderedList || 0) +
            (docMetrics?.numberOfInvalidOrderedList || 0),
        listCountNeeded: 0,
        get listCountCheckPass() {
            return this.listCount >= this.listCountNeeded && this.listCount <= MAX_LIST;
        },

        //keywords Handlers
        keywordCount: docMetrics?.keywords,
        keywordCountCheckPass: () => {
            let check = true;
            entity?.keywords?.forEach((keyword) => {
                if (
                    keyword &&
                    docMetrics?.keywords &&
                    docMetrics?.keywords[keyword] < NUMBER_OF_KEYWORD_PER_TEXT
                ) {
                    check = false;
                }
            });
            return check;
        },

        //links Handlers
        linksCount: docMetrics?.links,
        linksCheckPass: () => {
            if (!entity?.links) return true;
            return entity?.links.every((link) => {
                if (docMetrics?.links[link.anchor.toLowerCase()]?.badLink !== 0) {
                    return false;
                }
                return docMetrics?.links[link.anchor.toLowerCase()].urls.every((el) => {
                    return el.number === el.maxNumber;
                });
            });
        },
    };
};

export const allChecksPassed = (
    isForReview: boolean,
    userRole: UserRole,
    values: TValues,
    images: Array<any>,
    docMetrics?: TDocMetrics,
    entity?: Order,
    checkMinimumWords = true
) => {
    const {
        wordNumberCheckPass,
        metaTitleCheckPass,
        metaDescriptionCheckPass,
        h2CountCheckPass,
        existInvalidH2,
        h3CountCheckPass,
        existInvalidH3,
        linksCheckPass,
        keywordCountCheckPass,
        listCountCheckPass,
        boldCountCheckPass,
        italicCountCheckPass,
    } = getTextValidation(isForReview, values, docMetrics, entity, checkMinimumWords);

    if (
        !isForReview &&
        userRole !== UserRole.ROLE_LEAD_WRITER &&
        userRole !== UserRole.ROLE_WRITER
    ) {
        return linksCheckPass() && metaTitleCheckPass && metaDescriptionCheckPass;
    }

    //1/ WRITING_TYPE = AI_AUTO
    if (entity?.content?.writingType === WritingType.AI_AUTO) {
        return (
            linksCheckPass() &&
            images?.length > 0 &&
            metaTitleCheckPass &&
            metaDescriptionCheckPass &&
            !existInvalidH2 &&
            !existInvalidH3 &&
            listCountCheckPass &&
            boldCountCheckPass &&
            italicCountCheckPass &&
            wordNumberCheckPass
        );
    }

    //2/ WRITING_TYPE = AI_MANUAL
    if (entity?.content?.writingType === WritingType.AI_MANUAL) {
        return (
            metaTitleCheckPass &&
            metaDescriptionCheckPass &&
            !existInvalidH2 &&
            !existInvalidH3 &&
            listCountCheckPass &&
            boldCountCheckPass &&
            wordNumberCheckPass &&
            linksCheckPass()
        );
    }

    //4/ WRITING_TYPE = WRITE e writerNeeded = FALSE
    if (entity?.content?.writingType === WritingType.WRITE && !entity?.writerNeeded) {
        return (
            linksCheckPass() &&
            metaTitleCheckPass &&
            ((isForReview && images?.length > 0) || !isForReview) &&
            metaDescriptionCheckPass
        );
    }

    //1/ WRITING_TYPE = WRITE e writerNeeded = TRUE
    return (
        linksCheckPass() &&
        images?.length > 0 &&
        metaTitleCheckPass &&
        metaDescriptionCheckPass &&
        h2CountCheckPass &&
        !existInvalidH2 &&
        h3CountCheckPass &&
        !existInvalidH3 &&
        listCountCheckPass &&
        boldCountCheckPass &&
        italicCountCheckPass &&
        keywordCountCheckPass() &&
        wordNumberCheckPass
    );
};

export const contentHasChanged = (
    entity: Content,
    newData: {
        metaTitle: any;
        title: any;
        metaDescription: any;
        text: string;
        images: Array<ImageContent>;
    }
): boolean => {
    return (
        entity.title !== newData.title ||
        entity.metaTitle !== newData.metaTitle ||
        entity.metaDescription !== newData.metaDescription ||
        entity.text !== newData.text ||
        !arrayOfObjectAreEqual(entity.images, newData.images)
    );
};
